import React from 'react';
import { Grid, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ButtonLink, Icon, ImageAvatar, SquareChip, TagsArray, Text, useSettings, Utils } from 'app/main/components';
import { CurrencySquareChip, DateRangeSquareChip } from 'app/main/components/list-items';
import buttons from './OpportunitiesButtons';

export default function OpportunitiesListItem(props: ItemLayoutProps) {
	const { sysSettings, t } = useSettings();

	const isNotClosed = [1, 2].includes(props.model.oppo_opst_id);
	const daysToExpire = Utils.parseDate(props.model.oppo_d_to)?.diff(Utils.today(), 'days');

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="companies"
			uid={props.model.oppo_comp_uid}
			avatar={props.model.comp_avatar}
			initials={props.model.oppo_title_initials}
			labelBadge={props.model.opst_title_initials}
			labelBadgeTooltip={props.model.opst_title}
			badgeColor={props.model.opst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.oppo_warning_desc && (
								<Tooltip title={props.model.oppo_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.oppo_blocked_desc && (
								<Tooltip title={props.model.oppo_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'opportunities', uid: props.model.oppo_uid }}
							>
								{props.model.comp_title && `${props.model.comp_title} - `}
								{props.model.oppo_title}
							</Text>
						</Grid>
						{/* TYPE */}
						<Grid>
							{props.model.opty_title && (
								<SquareChip label={props.model.opty_title} backgroundColor={props.model.opty_color} />
							)}
						</Grid>
						{/* DATE FROM - TO */}
						<Grid>
							<DateRangeSquareChip
								from={props.model.oppo_d_from}
								to={props.model.oppo_d_to}
								icon={<Icon icon="clock-outline" size={16} />}
								backgroundColor={theme => {
									if (isNotClosed && daysToExpire !== undefined) {
										if (daysToExpire <= (sysSettings.opportunities_expiring_tier1 || 0))
											return theme.palette.error.main;
										if (daysToExpire <= sysSettings.opportunities_expiring_tier2)
											return theme.palette.warning.main;
									}
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={props.width >= 960 ? 3 : 6}>
						<Grid container>
							{/* INTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.oppo_user_assigned_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_INTUSER')}
								>
									{props.model.user_assigned_title}
								</ButtonLink>
							</Grid>
							{/* EXTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.oppo_user_assigned_2_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_EXTUSER')}
								>
									{props.model.user_assigned_2_title}
								</ButtonLink>
							</Grid>
						</Grid>
					</Grid>
				)}
				{props.width >= 960 && (
					<Grid size={3}>
						<Grid container>
							{/* VALUE */}
							<Grid>
								<CurrencySquareChip
									id="opportunitiesListItem.value"
									defaultMessage={t('VALUE')}
									value={props.model.oppo_value_planned}
								/>
							</Grid>
							{/* TAGS */}
							<Grid size={12}>
								<TagsArray tags={props.model.oppo_tags} changeFilter={props.changeFilter} />
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
